/**
 * Human labels for identity values, in one place so the app cannot drift.
 *
 * The GM reads these on screen and dictated the house style (2026-10-17): roles are
 * written as formal titles — “System Admin”, never “system admin” — and a company-wide
 * store scope is written as “All”.
 */
import { ROLE_LABELS, type RoleKey } from '../types/identity'

/** A person-shaped slice, so both `AuthUser` and `ManagedUser` can be passed in. */
interface RoleBearing {
  role: RoleKey
  is_platform_admin?: boolean
}

/**
 * The role as it should be read out loud.
 *
 * The operator role keeps its own name; the platform-admin flag is the one extra fact
 * worth surfacing, because it is what allows cross-company administration.
 */
export function roleLabel(person: RoleBearing): string {
  if (person.role === 'system_admin') return ROLE_LABELS.system_admin
  if (person.is_platform_admin) return 'Platform Admin'
  return ROLE_LABELS[person.role] ?? person.role
}

/** A person-shaped slice covering both single-store and multi-store scopes (D-42). */
interface StoreBearing {
  store_id: number | null
  scope_store_ids?: number[] | null
}

/** A person-shaped slice for the display-name rule (D-44). */
interface NameBearing {
  full_name: string
  nickname?: string | null
}

/**
 * What the app calls somebody: the **nickname** when they have one, otherwise their full
 * legal name. Formal surfaces — the account table, the audit trail, e-mails — always use
 * `full_name`; this is only for places that address the person directly.
 */
export function displayName(person: NameBearing): string {
  return person.nickname?.trim() || person.full_name
}

/**
 * The value shown under a “Store” heading.
 *
 * A district manager's selection reads as the list of shops they hold; everybody else
 * shows their one shop; company-wide roles — operations manager, platform admin —
 * show **All** (previously the word “company”, GM decision 2026-10-17).
 */
export function storeScopeLabel(person: StoreBearing): string {
  if (person.scope_store_ids && person.scope_store_ids.length > 0) {
    return person.scope_store_ids.join(', ')
  }
  return person.store_id ? String(person.store_id) : 'All'
}
